import React, { useEffect, useState } from 'react';
import '../css/accounting.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const CATEGORY_OPTIONS = [
  'Essentials',
  'Housing',
  'Utilities',
  'Groceries',
  'Transport',
  'Tech',
  'Payment plan',
  'Subscriptions',
  'Health',
  'Insurance',
  'Entertainment',
  'Travel',
  'Education',
  'Other'
];

function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [month, setMonth] = useState('');
  const [recurring, setRecurring] = useState(true);
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [transferAccountId, setTransferAccountId] = useState('');
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [statusMessage, setStatusMessage] = useState('');
  const [editExpense, setEditExpense] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [editRecurring, setEditRecurring] = useState(false);
  const [editTransferAccountId, setEditTransferAccountId] = useState('');
  const [editSourceAccountId, setEditSourceAccountId] = useState('');
  const [editEffectiveMonth, setEditEffectiveMonth] = useState('');
  const [editAmountOverrides, setEditAmountOverrides] = useState([]);
  const [editSourceMonth, setEditSourceMonth] = useState('');

  const user = localStorage.getItem('loggedInUsername');

  const loadExpenses = async (token, monthFilter) => {
    const response = await fetch('/api/accounting/expenses', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user, month: monthFilter })
    });

    if (!response.ok) {
      throw new Error('Failed to load expenses');
    }

    return response.json();
  };

  const loadAccounts = async (token) => {
    const response = await fetch('/api/accounting/accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load accounts');
    }

    return response.json();
  };

  const refreshExpenses = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const accountData = await loadAccounts(token);
      setAccounts(Array.isArray(accountData) ? accountData : []);
      const data = await loadExpenses(token, filterMonth || undefined);
      setExpenses(Array.isArray(data) ? data : []);
    } catch (error) {
      setStatusMessage('Unable to load expenses.');
    }
  };

  useEffect(() => {
    refreshExpenses();
  }, [filterMonth]);

  useEffect(() => {
    if (sourceAccountId || accounts.length === 0) {
      return;
    }
    const currentAccount = accounts.find((account) => account.account_type === 'current');
    if (currentAccount) {
      setSourceAccountId(String(currentAccount.id));
    }
  }, [accounts, sourceAccountId]);

  const handleAddExpense = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const resolvedSourceAccountId = sourceAccountId || null;
      const response = await fetch('/api/accounting/expenses/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          name,
          amount,
          category,
          month,
          recurring,
          transfer_account_id: transferAccountId || null,
          source_account_id: transferAccountId ? resolvedSourceAccountId : null
        })
      });

      if (response.ok) {
        setName('');
        setAmount('');
        setCategory('');
        setMonth('');
        setRecurring(true);
        setSourceAccountId('');
        setTransferAccountId('');
        setStatusMessage('Expense added.');
        refreshExpenses();
      } else {
        setStatusMessage('Unable to add expense.');
      }
    } catch (error) {
      setStatusMessage('Unable to add expense.');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/expenses/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user, expense_id: expenseId })
      });

      if (response.ok) {
        refreshExpenses();
      }
    } catch (error) {
      console.error('Unable to delete expense', error);
    }
  };

  const handleUpdateExpense = async (expense) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/expenses/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          expense_id: expense.id,
          name: expense.name,
          amount: expense.amount,
          category: expense.category,
          month: expense.month,
          recurring: expense.recurring,
          transfer_account_id: expense.transfer_account_id || null,
          source_account_id: expense.transfer_account_id ? (expense.source_account_id || null) : null
        })
      });

      if (response.ok) {
        setStatusMessage('Expense updated.');
        refreshExpenses();
      } else {
        setStatusMessage('Unable to update expense.');
      }
    } catch (error) {
      setStatusMessage('Unable to update expense.');
    }
  };

  const openEditModal = async (expense) => {
    setEditExpense(expense);
    setEditName(expense.name || '');
    setEditAmount(expense.amount || '');
    setEditCategory(expense.category || '');
    setEditMonth(expense.month || '');
    setEditRecurring(Boolean(expense.recurring));
    setEditTransferAccountId(expense.transfer_account_id ? String(expense.transfer_account_id) : '');
    setEditSourceAccountId(expense.source_account_id ? String(expense.source_account_id) : '');
    setEditEffectiveMonth('');
    setEditSourceMonth('');
    setEditAmountOverrides([]);

    if (expense.recurring) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await fetch('/api/accounting/expenses/amount-overrides', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ user, expense_id: expense.id })
          });
          if (response.ok) {
            const overrides = await response.json();
            setEditAmountOverrides(Array.isArray(overrides) ? overrides : []);
          }
        } catch (error) {
          // Overrides are optional context, not critical
        }
      }
    }
  };

  const closeEditModal = () => {
    setEditExpense(null);
    setEditName('');
    setEditAmount('');
    setEditCategory('');
    setEditMonth('');
    setEditRecurring(false);
    setEditTransferAccountId('');
    setEditSourceAccountId('');
    setEditEffectiveMonth('');
    setEditAmountOverrides([]);
    setEditSourceMonth('');
  };

  const handleSaveEdit = async () => {
    if (!editExpense) {
      return;
    }

    // If a recurring expense has an effective month set, use forward-only update for the amount
    if (editRecurring && editEffectiveMonth) {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return;
      }

      try {
        const response = await fetch('/api/accounting/expenses/update-from-month', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user,
            expense_id: editExpense.id,
            month_key: editEffectiveMonth,
            amount: editAmount
          })
        });

        if (response.ok) {
          setStatusMessage(`Expense amount updated from ${editEffectiveMonth} onward.`);
          refreshExpenses();
        } else {
          setStatusMessage('Unable to update expense amount.');
        }
      } catch (error) {
        setStatusMessage('Unable to update expense amount.');
      }

      closeEditModal();
      return;
    }

    const payload = {
      id: editExpense.id,
      name: editName,
      amount: editAmount,
      category: editCategory,
      month: editRecurring ? '' : editMonth,
      recurring: editRecurring,
      transfer_account_id: editTransferAccountId || null,
      source_account_id: editTransferAccountId ? (editSourceAccountId || null) : null
    };

    await handleUpdateExpense(payload);
    closeEditModal();
  };

  const getAccountName = (accountId, fallback = 'Account') => {
    return accounts.find((account) => account.id === accountId)?.name || fallback;
  };

  const handleEffectiveMonthChange = async (monthValue) => {
    setEditEffectiveMonth(monthValue);
    setEditSourceMonth('');

    if (!monthValue || !editExpense) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/expenses/effective-amount', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          expense_id: editExpense.id,
          month_key: monthValue
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEditAmount(data.amount || '');
        if (data.is_override && data.source_month) {
          setEditSourceMonth(data.source_month);
        }
      }
    } catch (error) {
      // Non-critical, keep current amount
    }
  };

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Monthly Expenses</h2>
            <p className="section-subtitle">Track recurring and one-off monthly obligations.</p>
          </div>
          <label>
            Filter month
            <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} />
          </label>
        </div>

        <div className="panel-grid">
          <section className="panel">
            <h3>Add Expense</h3>
            <p className="panel-sub">Recurring expenses apply to every month.</p>
            <div className="form-grid">
              <div className="form-row">
                <label>
                  Name
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label>
                  Amount
                  <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Category
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Month (optional)
                  <input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    disabled={recurring}
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Move funds to account (optional)
                  <select value={transferAccountId} onChange={(e) => setTransferAccountId(e.target.value)}>
                    <option value="">No transfer</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </select>
                </label>
                {transferAccountId && (
                  <label>
                    Source account
                    <select
                      value={sourceAccountId}
                      onChange={(e) => setSourceAccountId(e.target.value)}
                    >
                      <option value="">Current account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="form-row">
                <label className="inline-switch">
                  <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
                  Recurring each month
                </label>
                <button className="login-button" type="button" onClick={handleAddExpense}>Add expense</button>
              </div>
              {statusMessage && <span className="section-subtitle">{statusMessage}</span>}
            </div>
          </section>

          <section className="panel">
            <h3>Expense List</h3>
            <p className="panel-sub">Showing {filterMonth ? filterMonth : 'all months'}.</p>
            {expenses.length === 0 ? (
              <div className="empty-state">No expenses yet.</div>
            ) : (
              <div className="expense-list-scroll">
                <div className="expense-list">
                  {expenses.map((expense) => (
                    <div className="expense-row" key={expense.id}>
                      <div className="expense-row-main">
                        <span className="expense-row-title">{expense.name || 'Untitled expense'}</span>
                        <span className="expense-row-sub">{expense.category || 'Uncategorized'}</span>
                      </div>
                      <div className="expense-row-amount">
                        {formatCurrency(expense.amount)}
                        {expense.effective_source_month && (
                          <span className="expense-row-override-hint">from {expense.effective_source_month}</span>
                        )}
                      </div>
                      <div className="expense-row-actions">
                        <button className="login-button" type="button" onClick={() => openEditModal(expense)}>
                          Edit
                        </button>
                        <button className="login-button" type="button" onClick={() => handleDeleteExpense(expense.id)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </section>
      {editExpense && (
        <div className="expense-modal">
          <div className="expense-modal-card">
            <div className="expense-modal-header">
              <div>
                <h3>Edit Expense</h3>
                <p className="panel-sub">Update the details for this expense.</p>
              </div>
              <button className="login-button" type="button" onClick={closeEditModal}>Close</button>
            </div>
            <div className="form-grid">
              {editRecurring && (
                <div className="form-row">
                  <label>
                    Update amount from month
                    <input
                      type="month"
                      value={editEffectiveMonth}
                      onChange={(e) => handleEffectiveMonthChange(e.target.value)}
                    />
                  </label>
                  <div className="form-hint-container">
                    {editEffectiveMonth && (
                      <span className="section-subtitle">
                        Changes apply from {editEffectiveMonth} onward. Earlier months keep the previous amount.
                      </span>
                    )}
                    {editSourceMonth && editSourceMonth !== editEffectiveMonth && (
                      <span className="section-subtitle">
                        Currently using amount from {editSourceMonth}.
                      </span>
                    )}
                  </div>
                </div>
              )}
              {editRecurring && editAmountOverrides.length > 0 && (
                <div className="form-row">
                  <div className="override-history">
                    <span className="section-subtitle">Amount history:</span>
                    <ul className="override-list">
                      <li>Base: {formatCurrency(editExpense.amount)}</li>
                      {editAmountOverrides.map((ovr) => (
                        <li key={ovr.month_key}>From {ovr.month_key}: {formatCurrency(ovr.amount)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="form-row">
                <label>
                  Name
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} disabled={Boolean(editEffectiveMonth)} />
                </label>
                <label>
                  Amount
                  <input type="number" min="0" step="0.01" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Category
                  <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} disabled={Boolean(editEffectiveMonth)}>
                    <option value="">Select category</option>
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Month (optional)
                  <input
                    type="month"
                    value={editMonth}
                    onChange={(e) => setEditMonth(e.target.value)}
                    disabled={editRecurring || Boolean(editEffectiveMonth)}
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Move funds to account (optional)
                  <select value={editTransferAccountId} onChange={(e) => setEditTransferAccountId(e.target.value)} disabled={Boolean(editEffectiveMonth)}>
                    <option value="">No transfer</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </select>
                </label>
                {editTransferAccountId && (
                  <label>
                    Source account
                    <select value={editSourceAccountId} onChange={(e) => setEditSourceAccountId(e.target.value)} disabled={Boolean(editEffectiveMonth)}>
                      <option value="">Current account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="form-row">
                <label className="inline-switch">
                  <input type="checkbox" checked={editRecurring} onChange={(e) => setEditRecurring(e.target.checked)} disabled={Boolean(editEffectiveMonth)} />
                  Recurring each month
                </label>
                <button className="login-button" type="button" onClick={handleSaveEdit}>
                  {editEffectiveMonth ? 'Update from month' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
